import { TransportOutput } from '@allocab/allocab-commons-shared-types';
import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import clsx from 'clsx';
import { ChangeEvent, useState } from 'react';

import useFetchFlight from '../../../hooks/useFetchFlight';
import useFetchTrain from '../../../hooks/useFetchTrain';
import { t } from '../../../services/translation/translation';
import { TransportPickerType } from '../../../services/types';
import { formatDate } from '../../../utils/date';
import { TextInput } from '../../atomic/TextInput';
import { PrimaryButton } from '../../button/PrimaryButton';
import { ModalTitle } from '../../Modal';
import { Spinner } from '../../Spinner';

type Props = {
  trip: TripOutput;
  onValidate: (_transport: TransportOutput) => void;
  type: TransportPickerType;
};

export const TransportPickerStep1 = ({ onValidate, type, trip }: Props) => {
  const { mutate: fetchTrain, isLoading: isLoadingTrain } = useFetchTrain();
  const { mutate: fetchFlight, isLoading: isLoadingFlight } = useFetchFlight();
  const [number, setNumber] = useState('');
  const [carrier, setCarrier] = useState('');

  const arrivalDate = formatDate(trip.resume.departureAt, 'YYYY-MM-DD');

  const onChangeNumber = (event: ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value.replace(/\D+/gi, ''));
  };

  const onChangeCarrier = (event: ChangeEvent<HTMLInputElement>) => {
    setCarrier(event.target.value.replace(/[^a-zA-Z]/gi, ''));
  };

  const handleValidate = () => {
    if (type === 'train') {
      fetchTrain(
        {
          arrivalDate: arrivalDate,
          trainNumber: number,
          stationLat: trip.resume.start.lat,
          stationLng: trip.resume.start.lng,
        },
        {
          onSuccess: transport => {
            onValidate(transport);
          },
        },
      );
    } else {
      fetchFlight(
        {
          arrivalDate: arrivalDate,
          flightNumber: number,
          flightCarrier: carrier,
        },
        {
          onSuccess: transport => {
            onValidate(transport);
          },
        },
      );
    }
  };

  return (
    <div>
      <ModalTitle
        title={type === 'train' ? t('share-route.trip-modal.search-train') : t('share-route.trip-modal.search-flight')}
      />

      <div className={clsx('mt-4', type === 'flight' ? 'grid grid-cols-2 space-x-2' : 'grid')}>
        {type === 'flight' ? (
          <TextInput
            onChange={onChangeCarrier}
            value={carrier}
            type="text"
            label={t('transport-picker.code')}
            className="max-w-full border-none"
            placeholder={t('transport-picker.fligh-picker.placeholder.carrier')}
            autoCapitalize="characters"
          />
        ) : null}
        <TextInput
          onChange={onChangeNumber}
          value={number}
          type="text"
          label={t(
            type === 'train'
              ? 'transport-picker.train-picker.train-number'
              : 'transport-picker.fligh-picker.flight-number',
          )}
          className="max-w-full border-none"
          placeholder={t(
            type === 'train'
              ? 'transport-picker.train-picker.placeholder.train-number'
              : 'transport-picker.fligh-picker.placeholder.flight-number',
          )}
          autoCapitalize="characters"
        />
      </div>
      <div className="flex justify-end mt-5">
        <PrimaryButton onClick={() => handleValidate()}>
          {isLoadingFlight || isLoadingTrain ? <Spinner /> : t('common.search')}
        </PrimaryButton>
      </div>
    </div>
  );
};
